import { useParams, Link, useNavigate } from 'react-router-dom';
import { Archive, ArrowLeft, Plus } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MultiSelectPopover } from '@/components/ui/multi-select-popover';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';
import { usePartners, usePackages, useArchivedPackages, useUpdatePackage, usePartnershipsAccess, canEditSection } from '@/hooks/usePartnerships';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  normCode, sortedTiers, packageTiers, effectiveVariants, eligibilityFacets, matchesEligibility,
  EMPTY_ELIGIBILITY_SELECTION, type EligibilitySelection,
} from '../../../../shared/partnerships-packages';
import { PACKAGE_SHEET_ENUM_LABELS } from '../../../../shared/partnerships-package-sheet';
import { PackageCard, tierBadgeClass } from '@/components/partnerships/PackageCard';
import { PackageFormDialog } from '@/components/partnerships/PackageFormDialog';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PartnerSlug, Package as PartnerPackage } from '../../../../shared/types/partnerships';

type TFunction = ReturnType<typeof useTranslation>['t'];

const PARTNER_ACCENT: Record<string, { text: string; activeBorder: string; activeBg: string; eyebrow: string }> = {
  tiktok:  { text: 'text-blue-600',   activeBorder: 'border-blue-300',   activeBg: 'bg-blue-50',   eyebrow: 'text-blue-600' },
  google:  { text: 'text-red-600',    activeBorder: 'border-red-300',    activeBg: 'bg-red-50',    eyebrow: 'text-red-600' },
  meta:    { text: 'text-purple-600', activeBorder: 'border-purple-300', activeBg: 'bg-purple-50', eyebrow: 'text-purple-600' },
};

// Función de `t`: los textos salen de i18n y `t` no existe a nivel de módulo.
const SALE_TYPE_META = (t: TFunction): Record<string, { label: string; sublabel: string; desc: string }> => ({
  agency_sold: {
    label: t('partnerships.packages.agency_sold'),
    sublabel: t('partnerships.packages.winclap_cierra_y_ejecuta'),
    desc: t('partnerships.packages.winclap_lidera_la_propuesta_y_cierra'),
  },
  google_sold: {
    label: t('partnerships.packages.google_sold'),
    sublabel: t('partnerships.packages.google_cierra_winclap_ejecuta'),
    desc: t('partnerships.packages.el_equipo_de_ventas_de_google'),
  },
});

export function PartnerPackagesPage() {
  const { t } = useTranslation();
  const { partner: slug } = useParams<{ partner: string }>();
  const navigate = useNavigate();
  const [selectedSaleType, setSelectedSaleType] = useState<string | null>(null);
  const [selectedSubType, setSelectedSubType] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const [eligibility, setEligibility] = useState<EligibilitySelection>(EMPTY_ELIGIBILITY_SELECTION);
  const [editPackage, setEditPackage] = useState<PartnerPackage | undefined>();
  const [createPackage, setCreatePackage] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<PartnerPackage | null>(null);
  const updatePackage = useUpdatePackage();
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const { data: access } = usePartnershipsAccess();
  const { data: partners = [], isPending: partnersPending } = usePartners();
  const { setPartnerCtx } = usePageContext();

  const partnerSlug = slug as PartnerSlug;
  const config = PARTNER_CONFIG[partnerSlug];

  const partner = partners.find(p => p.slug === partnerSlug);
  const partnerId = partner?.partnerId || '';
  const canEdit = canEditSection(access?.editScopes, 'all');

  useEffect(() => {
    if (!partnerId || !config) return;
    setPartnerCtx({ partnerId, partnerName: config.name, partnerSlug });
    return () => setPartnerCtx(null);
  }, [partnerId, partnerSlug]);

  const { data: packages = [], isPending: packagesPending } = usePackages(partnerId);
  // "no packages" a la primera (feedback 07/10/2026): con la query deshabilitada mientras
  // cargan los partners, react-query v5 da isLoading=false y la página mostraba vacío.
  const isLoading = partnersPending || (!!partnerId && packagesPending);
  const { data: archived = [], isPending: archivedPending } = useArchivedPackages(partnerId, showArchived);

  /** true si se guardó. El error ya lo muestra el toast del hook (onError). */
  const archive = async (p: PartnerPackage): Promise<boolean> => {
    try {
      // Se guarda el estado previo: "Restaurar" vuelve a ese (un borrador no queda publicado).
      await updatePackage.mutateAsync({ packageId: p.packageId, status: 'ARCHIVED', statusBeforeArchive: p.status, expectedUpdatedAt: p.updatedAt });
      return true;
    } catch {
      return false;
    }
  };
  const restore = async (p: PartnerPackage) => {
    setRestoringId(p.packageId);
    try {
      const back = p.statusBeforeArchive && p.statusBeforeArchive !== 'ARCHIVED' ? p.statusBeforeArchive : 'PUBLISHED';
      await updatePackage.mutateAsync({ packageId: p.packageId, status: back, expectedUpdatedAt: p.updatedAt });
    } catch {
      /* el toast lo muestra el hook */
    } finally {
      setRestoringId(null);
    }
  };

  const hasSaleTypes = useMemo(() => packages.some(p => p.saleType), [packages]);

  const subTypeGroups = useMemo(() => {
    if (selectedSaleType !== 'google_sold') return [];
    const set = new Set<string>();
    packages.filter(p => p.saleType === 'google_sold').forEach(p => {
      const st = normCode(p.saleSubType);
      if (st) set.add(st);
    });
    return Array.from(set);
  }, [packages, selectedSaleType]);

  const hasSubTypes = subTypeGroups.length > 1;

  const saleTypeGroups = useMemo(() => {
    if (!hasSaleTypes) return [];
    const map = new Map<string, { label: string; sublabel: string; desc: string; tiers: string[] }>();
    for (const p of packages) {
      const key = p.saleType || 'other';
      if (!map.has(key)) {
        const meta = SALE_TYPE_META(t)[key] ?? { label: key, sublabel: '', desc: '' };
        map.set(key, { ...meta, tiers: [] });
      }
      // Los tiers ahora viven en las variantes del pack (13/10/2026).
      map.get(key)!.tiers.push(...packageTiers(p));
    }
    return Array.from(map.entries()).map(([key, val]) => ({ key, ...val, tiers: sortedTiers(val.tiers) }));
  }, [packages, hasSaleTypes, t]);

  // Elegibilidad como filtro (pedido de Partnerships 13/10/2026): opciones derivadas de los packs.
  const facets = useMemo(() => eligibilityFacets(packages), [packages]);
  const facetOptions = (m: Map<string, number>, labelOf?: (v: string) => string) =>
    [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([value, count]) => ({ value, count, label: labelOf?.(value) }));
  const hasEligibility = facets.segments.size + facets.accountSizes.size + facets.clientTypes.size > 0;

  const visiblePackages = useMemo(() => {
    let pkgs = packages;
    if (hasSaleTypes && selectedSaleType) pkgs = pkgs.filter(p => p.saleType === selectedSaleType);
    if (hasSubTypes && selectedSubType) pkgs = pkgs.filter(p => normCode(p.saleSubType) === selectedSubType);
    if (selectedMarket) pkgs = pkgs.filter(p => p.availableMarkets.includes(selectedMarket));
    pkgs = pkgs.filter(p => matchesEligibility(p, eligibility));
    return pkgs;
  }, [packages, hasSaleTypes, selectedSaleType, hasSubTypes, selectedSubType, selectedMarket, eligibility]);

  const packageMarkets = useMemo(() => {
    let pkgs = hasSaleTypes && selectedSaleType
      ? packages.filter(p => p.saleType === selectedSaleType)
      : packages;
    if (hasSubTypes && selectedSubType) pkgs = pkgs.filter(p => normCode(p.saleSubType) === selectedSubType);
    const set = new Set<string>();
    for (const p of pkgs) p.availableMarkets.forEach(m => set.add(m));
    return Array.from(set).sort();
  }, [packages, hasSaleTypes, selectedSaleType, hasSubTypes, selectedSubType]);

  const activeSaleTypeMeta = selectedSaleType ? SALE_TYPE_META(t)[selectedSaleType] : null;
  const hasRowData = packages.some(p => effectiveVariants(p).length > 0);
  const accent = PARTNER_ACCENT[partnerSlug] ?? PARTNER_ACCENT.tiktok;

  if (!config) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('partnerships.common.partner_not_found')}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/partnerships')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.ui.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page hero */}
      <div className="border-b border-border pb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={`/partnerships/${partnerSlug}`} className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> {config.name}
          </Link>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className={`text-[10px] font-semibold tracking-[0.15em] uppercase mb-2 ${accent.eyebrow}`}>
              {config.name}
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{t('partnerships.packages.title')}</h1>
            <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
              {t('partnerships.packages.description')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant={showArchived ? 'secondary' : 'ghost'} className="gap-1.5" onClick={() => setShowArchived(v => !v)} aria-pressed={showArchived}>
              <Archive className="h-3.5 w-3.5" /> {showArchived ? t('partnerships.packages.hide_archived') : t('partnerships.packages.show_archived')}
            </Button>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setCreatePackage(true)}>
                <Plus className="h-3.5 w-3.5" /> {t('partnerships.packages.new_button')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><WinclapLoader /></div>
      ) : packages.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">{t('partnerships.packages.empty')}</div>
      ) : (
        <div className="flex flex-col gap-8">
          {/* Deal-type selector — only when saleType is used */}
          {hasSaleTypes && saleTypeGroups.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {saleTypeGroups.map(group => {
                const isSelected = selectedSaleType === group.key;
                return (
                  <button
                    key={group.key}
                    onClick={() => {
                      setSelectedSaleType(isSelected ? null : group.key);
                      setSelectedMarket(null);
                      setSelectedSubType(null);
                    }}
                    className={`text-left p-5 rounded-lg border-2 transition-all ${
                      isSelected
                        ? `${accent.activeBorder} ${accent.activeBg} shadow-xs`
                        : 'border-border bg-card hover:border-border/80'
                    }`}
                  >
                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${accent.text}`}>
                      {group.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-3">
                      {t(`partnerships.packages.sale_type_${group.key}_sublabel`)}
                    </div>
                    {group.desc && (
                      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                        {t(`partnerships.packages.sale_type_${group.key}_desc`)}
                      </p>
                    )}
                    {group.tiers.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {group.tiers.map(tier => (
                          <span key={tier} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tierBadgeClass(tier)}`}>
                            {tier}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* GCS / LCS sub-type selector — only inside Google Sold */}
          {hasSubTypes && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mr-1">{t('partnerships.packages.filter_type')}</span>
              {(['all', ...subTypeGroups] as string[]).map(st => (
                <button
                  key={st}
                  onClick={() => { setSelectedSubType(st === 'all' ? null : st); setSelectedMarket(null); }}
                  className={`px-3.5 py-1.5 rounded-md border text-xs font-medium transition-all ${
                    (st === 'all' && !selectedSubType) || selectedSubType === st
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/30'
                  }`}
                >
                  {st === 'all' ? t('partnerships.packages.filter_all') : st.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {/* Market filter */}
          {packageMarkets.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mr-1">{t('partnerships.packages.filter_market')}</span>
              <button
                onClick={() => setSelectedMarket(null)}
                className={`px-3.5 py-1.5 rounded-md border text-xs font-medium transition-all ${
                  !selectedMarket
                    ? 'bg-foreground text-background border-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/30'
                }`}
              >
                {t('partnerships.packages.filter_all')}
              </button>
              {packageMarkets.map(m => (
                <button
                  key={m}
                  onClick={() => setSelectedMarket(selectedMarket === m ? null : m)}
                  className={`px-3.5 py-1.5 rounded-md border text-xs font-medium transition-all ${
                    selectedMarket === m
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/30'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* Elegibilidad: segmento, tamaño de cuenta, nuevo vs recurrente — solo si algún pack lo carga */}
          {hasEligibility && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mr-1">{t('partnerships.packages.filter_eligibility')}</span>
              {facets.segments.size > 0 && (
                <MultiSelectPopover
                  label={t('partnerships.packages.filter_segment')}
                  options={facetOptions(facets.segments)}
                  selected={eligibility.segments}
                  onChange={segments => setEligibility(e => ({ ...e, segments }))}
                  allLabel={t('partnerships.packages.filter_all')}
                />
              )}
              {facets.accountSizes.size > 0 && (
                <MultiSelectPopover
                  label={t('partnerships.packages.filter_account_size')}
                  options={facetOptions(facets.accountSizes)}
                  selected={eligibility.accountSizes}
                  onChange={accountSizes => setEligibility(e => ({ ...e, accountSizes }))}
                  allLabel={t('partnerships.packages.filter_all')}
                />
              )}
              {facets.clientTypes.size > 0 && (
                <MultiSelectPopover
                  label={t('partnerships.packages.filter_client_type')}
                  options={facetOptions(facets.clientTypes, v => t(`partnerships.package_sheet.enums.${v}`, { defaultValue: PACKAGE_SHEET_ENUM_LABELS[v] ?? v }))}
                  selected={eligibility.clientTypes}
                  onChange={clientTypes => setEligibility(e => ({ ...e, clientTypes }))}
                  allLabel={t('partnerships.packages.filter_all')}
                />
              )}
            </div>
          )}

          {/* Grid context label + packages */}
          <div>
            {(activeSaleTypeMeta || selectedMarket) && (
              <div className="flex items-center gap-2 mb-5">
                {activeSaleTypeMeta && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${accent.text}`}>
                    {activeSaleTypeMeta.label}
                  </span>
                )}
                {activeSaleTypeMeta && selectedMarket && (
                  <span className="text-muted-foreground text-xs">·</span>
                )}
                {selectedMarket && (
                  <span className="text-xs text-muted-foreground">{selectedMarket}</span>
                )}
              </div>
            )}

            {visiblePackages.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">{t('partnerships.packages.empty_filtered')}</div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visiblePackages.map(p => (
                  <PackageCard
                    key={p.packageId} pkg={p} partnerSlug={partnerSlug}
                    canEdit={canEdit} onEdit={setEditPackage} onArchive={setConfirmArchive}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Glossary — only when packages have row data */}
          {hasRowData && (
            <div className="bg-muted/20 border border-border/50 rounded-lg p-5">
              <div className="text-xs font-semibold text-foreground mb-4">{t('partnerships.packages.glossary_title')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground">{t('partnerships.package_detail.variant_videos')}</span> — {t('partnerships.packages.glossary_videos')}</div>
                <div><span className="font-medium text-foreground">{t('partnerships.packages.min_creators')}</span> — {t('partnerships.packages.glossary_min_creators')}</div>
                <div><span className="font-medium text-foreground">{t('partnerships.packages.production_fee')}</span> — {t('partnerships.packages.glossary_production_fee')}</div>
                <div><span className="font-medium text-foreground">{t('partnerships.packages.media_commitment')}</span> — {t('partnerships.packages.glossary_media_commitment')}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Archivados: se piden recién al abrir la vista; restaurar = volver a Publicado */}
      {showArchived && (
        <div className="rounded-lg border border-dashed border-border p-5">
          <div className="mb-4 flex items-center gap-2 text-xs font-semibold text-foreground">
            <Archive className="h-3.5 w-3.5" /> {t('partnerships.packages.archived_title')}
          </div>
          {archivedPending ? (
            <div className="flex justify-center py-8"><WinclapLoader /></div>
          ) : archived.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('partnerships.packages.archived_empty')}</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {archived.map(p => (
                <PackageCard
                  key={p.packageId} pkg={p} partnerSlug={partnerSlug}
                  canEdit={canEdit} onRestore={canEdit ? (pkg) => { void restore(pkg); } : undefined}
                  restoring={restoringId === p.packageId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!confirmArchive} onOpenChange={o => { if (!o) setConfirmArchive(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('partnerships.packages.archive_confirm_title', { name: confirmArchive?.name || '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('partnerships.packages.archive_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={updatePackage.isPending}
              onClick={async (e) => {
                // El diálogo se cierra recién cuando el archivado terminó bien.
                e.preventDefault();
                if (confirmArchive && (await archive(confirmArchive))) setConfirmArchive(null);
              }}
            >
              {t('partnerships.packages.archive_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {partnerId && (
        <PackageFormDialog
          open={createPackage || !!editPackage}
          onOpenChange={o => { if (!o) { setCreatePackage(false); setEditPackage(undefined); } }}
          partnerId={partnerId}
          pkg={editPackage}
        />
      )}
    </div>
  );
}
